import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";

// ===== Track A: Self-healing auth boot =====
// Sanity-check sb-*-auth-token entries in localStorage BEFORE any Supabase
// code imports/runs. If a token is corrupt or unparseable, the SDK can throw
// during boot and Safari/WebKit surfaces it as "TypeError: Load failed".
// This purges bad entries so the SDK starts clean. Idempotent for healthy tokens.
(function purgeCorruptSupabaseTokens() {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith('sb-') || !key.endsWith('-auth-token')) continue;
      const raw = localStorage.getItem(key);
      if (!raw) {
        keysToRemove.push(key);
        continue;
      }
      try {
        const parsed = JSON.parse(raw);
        const hasShape =
          parsed &&
          typeof parsed === 'object' &&
          typeof parsed.access_token === 'string' &&
          typeof parsed.refresh_token === 'string' &&
          (typeof parsed.expires_at === 'number' || typeof parsed.expires_at === 'undefined');
        if (!hasShape) {
          keysToRemove.push(key);
          continue;
        }
        // Purge tokens whose absolute expiry is more than 30 days in the past
        if (typeof parsed.expires_at === 'number') {
          const ageSec = Math.floor(Date.now() / 1000) - parsed.expires_at;
          if (ageSec > 60 * 60 * 24 * 30) {
            keysToRemove.push(key);
          }
        }
      } catch {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => {
      try { localStorage.removeItem(k); } catch {}
    });
    if (keysToRemove.length > 0) {
      console.warn('[boot] purged corrupt/expired sb-* tokens:', keysToRemove.length);
    }
  } catch (e) {
    console.warn('[boot] token sanity check failed (non-fatal):', e);
  }
})();

import { logSecurityEvent, getCachedUserId } from "@/utils/securityLogger";

// Global error handler for uncaught JavaScript errors.
// Uses the cached user id — never awaits a network auth call, so the log is
// still written when the app is crashing or offline.
window.addEventListener('error', (event) => {
  // Skip certain benign errors
  if (event.message?.includes('ResizeObserver') || event.message?.includes('Script error')) {
    return;
  }

  logSecurityEvent(
    'client_unhandled_error',
    getCachedUserId(),
    `Uncaught error: ${event.message?.slice(0, 150) || 'Unknown'}`,
    {
      filename: event.filename?.slice(-100) || 'unknown',
      lineno: event.lineno || 0,
      colno: event.colno || 0,
      page_url: window.location.pathname,
    }
  );
});

// Global handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason instanceof Error 
    ? event.reason.message 
    : String(event.reason);
  
  // Skip auth-related rejections
  if (reason?.includes('User not authenticated') || reason?.includes('JWT')) {
    return;
  }
  
  logSecurityEvent(
    'client_promise_rejection',
    getCachedUserId(),
    `Unhandled rejection: ${reason?.slice(0, 150) || 'Unknown'}`,
    {
      page_url: window.location.pathname,
      stack: event.reason?.stack?.slice(0, 300) || null,
    }
  );
});


// Service worker registration removed.
// The kill-switch in index.html unregisters any prior SW. We are not
// re-registering one until login stability is confirmed in production.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => r.unregister().catch(() => {}));
  }).catch(() => {});
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>
);
